// @ts-nocheck
import { useState, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  IconBriefcase, IconFileDescription, IconFileInvoice, IconCalendar,
  IconChevronRight, IconCalendarEvent, IconClock, IconMapPin,
} from '@tabler/icons-react';
import Fetch from '@/utils/Fetch';
import Time from '@/utils/time';
import PageContext from '@/utils/page-context';
import { useCustomerZoneCompany } from '@/utils/useCustomerZoneCompany';
import styles from '../CustomerZoneDetail.module.css';

const TABS = [
  { key: 'jobs',         label: 'Jobs',         Icon: IconBriefcase },
  { key: 'quotes',       label: 'Quotes',       Icon: IconFileDescription },
  { key: 'invoices',     label: 'Invoices',     Icon: IconFileInvoice },
  { key: 'appointments', label: 'Appointments', Icon: IconCalendar },
];

const LIST_PAGES: Record<string, string> = { jobs: 'joblist', quotes: 'quotelist', invoices: 'invoicelist', appointments: 'appointmentlist' };

function Pill({ text, tone }: { text: string; tone: string }) {
  const cls = { ok: styles.pillOk, warn: styles.pillWarn, danger: styles.pillDanger, info: styles.pillInfo }[tone] || styles.pillInfo;
  return <span className={`${styles.pill} ${cls}`}>{text}</span>;
}

function ApptCard({ appt, isToday, href, onClick }: any) {
  const dateLabel = appt.StartDateTime
    ? Time.toISOString(Time.parseDate(appt.StartDateTime), false, false)
    : '';
  const timeLabel = appt.StartDateTime
    ? Time.toISOString(Time.parseDate(appt.StartDateTime), false, true, false, null, false)
    : '';
  const title = appt.Subject || dateLabel || 'Appointment';
  return (
    <a className={styles.miniCard} href={href} onClick={(e) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
      e.preventDefault();
      onClick();
    }}>
      <div className={styles.miniHead}>
        <span className={styles.miniTitle}>{title}</span>
        <Pill text={isToday ? 'Today' : 'Upcoming'} tone={isToday ? 'info' : 'success'} />
      </div>
      <div className={styles.miniRows}>
        {dateLabel && (
          <div className={styles.miniRow}>
            <span className={styles.miniRowIcon}><IconCalendarEvent size={13} /></span>
            <span>{dateLabel}</span>
          </div>
        )}
        {timeLabel && (
          <div className={styles.miniRow}>
            <span className={styles.miniRowIcon}><IconClock size={13} /></span>
            <span>{timeLabel}</span>
          </div>
        )}
        {appt.Location?.AddressLine1 && (
          <div className={styles.miniRow}>
            <span className={styles.miniRowIcon}><IconMapPin size={13} /></span>
            <span>{appt.Location.AddressLine1}</span>
          </div>
        )}
      </div>
    </a>
  );
}

function Group({ label, tone, appts, getHref, onCard }: any) {
  if (!appts.length) return null;
  const cls = { success: styles.groupLabelSuccess, ok: styles.groupLabelOk, warn: styles.groupLabelWarn, danger: styles.groupLabelDanger, info: styles.groupLabelInfo }[tone] || styles.groupLabelInfo;
  const isToday = tone === 'info';
  return (
    <div className={styles.listGroup}>
      <div className={`${styles.groupLabel} ${cls}`}>
        {label}<span className={styles.gCount}>({appts.length})</span>
      </div>
      <div className={styles.grid}>
        {appts.map((appt: any, i: number) => (
          <ApptCard key={i} appt={appt} isToday={isToday} href={getHref(appt)} onClick={() => onCard(appt)} />
        ))}
      </div>
    </div>
  );
}

export default function CustomerZoneAppointmentListPage() {
  const router = useRouter();
  const { t, c } = router.query;
  const pageContext = useContext(PageContext) as any;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<any[]>([]);

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0
    ? tenantData.Results[0] : null;

  useEffect(() => {
    if (tenantData && !tenant) router.replace('/');
  }, [tenantData]);

  const { data: now } = useQuery(
    ['czNow', t, c],
    () => Time.nowCZ(t, c, tenant.API),
    { enabled: !!tenant }
  );

  const { data: apptData, isFetching } = useQuery(
    ['czApptList', t, c, page],
    () => Fetch.post({ url: '/CustomerZone/GetAppointments', params: { PageSize: 50, PageIndex: page, SortExpression: 'StartDateTime', SortDirection: 'ascending' }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant, keepPreviousData: true }
  );

  const { data: company } = useCustomerZoneCompany(t, c, tenant?.API);

  useEffect(() => {
    if (!apptData?.Results) return;
    if (page === 0) setAccumulated(apptData.Results);
    else setAccumulated(prev => [...prev, ...apptData.Results]);
  }, [apptData]);

  useEffect(() => {
    const name = apptData?.headerTenantName || tenant?.Name;
    if (name) pageContext?.updateTenantName?.(name);
  }, [apptData, tenant]);

  if (!tenant) return null;

  const base = `?t=${t}&c=${c}`;
  const logoUrl = company?.LogoUrl || null;
  const companyName = company?.Name || tenant?.Name || '';

  const allAppts = accumulated;
  const hasMore = (apptData?.Results?.length ?? 0) >= 50;
  const isEmpty = allAppts.length === 0 && !!apptData;

  const today = now ? Time.parseDate(Time.today(now)) : new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todayAppts    = allAppts.filter((a: any) => { const d = Time.parseDate(a.StartDateTime); return d >= today && d < tomorrow; });
  const upcomingAppts = allAppts.filter((a: any) => Time.parseDate(a.StartDateTime) >= tomorrow);

  const navigate = (tab: string) => router.push(`/customerzone/${LIST_PAGES[tab]}${base}`);
  const goToDetail = (appt: any) => router.push(`/customerzone/viewappointment${base}&i=${appt.ID}`);
  const getHref = (appt: any) => `/customerzone/viewappointment${base}&i=${appt.ID}`;

  return (
    <>
      <div className={styles.navWrap}>
        <header className={styles.nav}>
          <div className={styles.navInner}>
            <a className={styles.navLogo} href={`/customerzone${base}`}>
              <img src="/logo-type-white.svg" alt="ServCraft" onError={(e: any) => { e.target.style.display = 'none'; }} />
            </a>
            <div className={styles.navRight}>
              <a className={styles.btnTrial} href="https://servcraft.co.za/sign-up" target="_blank" rel="noopener">
                Start your free trial
              </a>
            </div>
          </div>
        </header>
      </div>
      <div className={styles.root}>
        <div className={styles.page}>
          <div className={styles.wrap}>
            <div className={styles.portalId}>
              <div className={`${styles.portalBadge} ${!logoUrl ? styles.isFallback : ''}`}>
                {logoUrl ? (
                  <img src={logoUrl} alt={companyName} />
                ) : (
                  <span className={styles.portalBadgeFallback}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 9h.01M15 9h.01M9 15h6" />
                    </svg>
                  </span>
                )}
              </div>
              <p className={styles.portalName}>{companyName}</p>
            </div>
          <div className={styles.card}>
            <div className={styles.switchWrap}>
              <div className={styles.cardToolbar}>
                <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                  <Link to="/customerzone" search={{ t, c }}>Portal</Link>
                  <span className={styles.bcSep}><IconChevronRight /></span>
                  <span className={styles.bcCurrent}>Appointments</span>
                </nav>
              </div>
              <div className={styles.switchTabs}>
                {TABS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    className={`${styles.switchTab}${key === 'appointments' ? ` ${styles.switchTabActive}` : ''}`}
                    onClick={() => navigate(key)}
                  >
                    <Icon size={16} /><span>{label}</span>
                  </button>
                ))}
              </div>
              <div className={`${styles.switchSelect}${mobileOpen ? ` ${styles.switchSelectOpen}` : ''}`}>
                <button className={styles.switchTrigger} onClick={() => setMobileOpen(o => !o)} type="button">
                  <span className={styles.switchTriggerLeft}>
                    <IconCalendar size={15} />
                    <span>Viewing</span>
                    <span className={styles.switchTriggerCurrent}>Appointments</span>
                  </span>
                  <IconChevronRight className={styles.switchTriggerChev} size={14} />
                </button>
                <div className={styles.switchMenu}>
                  {TABS.map(({ key, label, Icon }) => (
                    <button key={key} className={`${styles.switchOpt}${key === 'appointments' ? ` ${styles.switchOptActive}` : ''}`} onClick={() => { setMobileOpen(false); navigate(key); }}>
                      <Icon size={17} /><span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.cardInner}>
              <h2 className={styles.contentHead}>Your Appointments</h2>
              {isEmpty ? (
                <p className={styles.emptyNote}>There are no appointments</p>
              ) : (
                <div className={styles.listGroups}>
                  <Group label="Today" tone="info" appts={todayAppts} getHref={getHref} onCard={goToDetail} />
                  <Group label="Upcoming" tone="success" appts={upcomingAppts} getHref={getHref} onCard={goToDetail} />
                  {hasMore && (
                    <div className={styles.loadMore}>
                      <button className={styles.loadMoreBtn} onClick={() => setPage(p => p + 1)} disabled={isFetching}>
                        {isFetching ? 'Loading…' : 'Load more'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={styles.support}>
            <span className={styles.sTitle}>Need support?</span>
            <p>Contact us at <a href="mailto:support@servcraft.co.za">support@servcraft.co.za</a></p>
          </div>
          </div>{/* wrap */}
        </div>{/* page */}
      </div>{/* root */}
    </>
  );
}
